<template>
    <div class="animated fadeIn">
        <kgr-note-box text-key="kindergarten.baykibig.childYearBatch.note" />
        <br />
        <kgr-table
            :is-data-table="false"
            not-striped
            style="width: 100%"
        >
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        <kgr-checkbox :checked="allChecked" @click="toggleAll" />
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Kind') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Förderstatus') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Faktor') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Nutzungszeit') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Faktor') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('von') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('bis') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Vorschulkind') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('beitragsfrei') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('nicht förderfähig') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Abweichender Gemeindeschlüssel') }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(data, index) in tableBatchData"
                    :key="index"
                    :class="{
                        'is-striped': data.rowIndex % 2 === 1,
                        'batch-result-fail': hasFailed(data.child.id),
                        'batch-result-success': hasSucceeded(data.child.id, data.membership?.id),
                    }"
                    class="kgr-table__row"
                >
                    <td
                        v-if="data.rowspan"
                        class="kgr-table__cell kgr-table__cell--actions"
                        :rowspan="data.rowspan"
                    >
                        <kgr-checkbox
                            :checked="selectedChildren.includes(data.child.id)"
                            style="display: inline-block"
                            @click="toggleChild(data.child)"
                        />
                        <kgr-icon
                            v-if="hasFailed(data.child.id)"
                            icon-class="warning"
                            small
                            style="display: inline-block"
                            :title="getFailedTooltip(data.child.id)"
                            class="kgr-icon--warning"
                        />
                    </td>
                    <td
                        v-if="data.rowspan"
                        class="kgr-table__cell"
                        :rowspan="data.rowspan"
                    >
                        <a
                            :href="`/backend/child/${data.child.id}/costs/baykibig/group-membership`"
                            class="kgr-link"
                        >
                            {{ data.child.firstName }} {{ data.child.lastName }}
                        </a>
                    </td>
                    <td class="kgr-table__cell">
                        {{ data.membership?.groupFormVariantCombination.name }}
                    </td>
                    <td class="kgr-table__cell">
                        {{ data.membership?.groupFormVariantCombination.factor }}
                    </td>
                    <td class="kgr-table__cell">
                        {{ data.membership?.careTimeVariant.name }}
                    </td>
                    <td class="kgr-table__cell">
                        {{ data.membership?.careTimeVariant.factor }}
                    </td>
                    <td class="kgr-table__cell">
                        {{ data.membership?.start.toString() }}
                    </td>
                    <td class="kgr-table__cell">
                        {{ data.membership?.end }}
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-icon
                            v-if="data.membership?.preschooler"
                            icon-class="check"
                        />
                        <span v-else-if="data.membership">-</span>
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-icon
                            v-if="data.membership?.nonContributory"
                            icon-class="check"
                        />
                        <span v-else-if="data.membership">-</span>
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-icon
                            v-if="data.membership?.notEligible"
                            icon-class="check"
                        />
                        <span v-else-if="data.membership">-</span>
                    </td>
                    <td class="kgr-table__cell">
                        {{ data.membership?.municipalityId }}
                    </td>
                </tr>
            </tbody>
        </kgr-table>

        <kgr-empty-data
            v-if="!loading && batchData.length === 0"
            :value="$t('dataTable.noData')"
        />
        <br />
        <div class="kgr-postfix kgr-postfix--bigGutters kgr-postfix--center kgr-row">
            <div class="kgr-postfix__fluid"></div>
            <div class="kgr-postfix__fixed kgr-postfix__fixed--noShrink kgr-hidden-print js-test-actionButtonsBlock">
                <kgr-button
                    icon-add
                    :value="$t('Einträge berechnen')"
                    :disabled="selectedChildren.length === 0"
                    @click="openSaveModal"
                />
            </div>
        </div>
        <kgr-generic-modal :value="showSaveModal" @close="showSaveModal = false" :headline="$t('Einträge berechnen')">
            <template #default>
                <p v-if="batchResult === null">
                    {{ $t('Möchten Sie einen BayKiBiG Eintrag für ein ausgewähltes Kind berechnen lassen? | Möchten Sie die BayKiBiG Einträge für {count} ausgewählte Kinder berechnen lassen?', { count: selectedChildren.length }) }}
                </p>
                <p v-else>
                    {{ $t('Die Berechnung ist beendet.') }}
                </p>
                <kgr-loading v-if="pending" style="margin-top:20px" />
                <p v-else-if="batchResult !== null" class="kgr-text kgr-text--bold">
                    <br />
                    {{ $tc('Es wurde 1 Eintrag erstellt. | Es wurden {count} Einträge erstellt.', { count: batchResult.filter(r => r.success).length }) }}<br />
                    <br />
                    {{ $t('Für ein Kind konnten keine Eintrage berechnet werden. | Für {count} Kinder konnten keine Einträge berechnet werden.', { count: batchResult.filter(r => !r.success).length }) }}<br />
                </p>
            </template>
            <template #footer>
                <kgr-button
                    v-if="batchResult === null"
                    class="kgr-button--outline"
                    :value="$t('Abbrechen')"
                    @click="showSaveModal = false"
                />
                <kgr-button
                    v-if="batchResult === null"
                    :value="$t('Einträge berechnen')"
                    :pending="pending"
                    @click="generate"
                />
                <kgr-button
                    v-else
                    :value="$t('Schließen')"
                    @click="finishSaveModal"
                />
            </template>
        </kgr-generic-modal>
    </div>
</template>

<script>
import Checkbox from '../../../../components/common/form/checkbox/Checkbox.vue';
import EmptyData from '../../../../components/common/EmptyData.vue';
import Error from '../../../../js/model/Error.js';
import GenericModal from '../../../../components/common/modal/GenericModal.vue';
import Icon from '../../../../components/common/icon/Icon.vue';
import KgrButton from '../../../../components/common/form/button/KgrButton.vue';
import KgrDate from '../../../../js/date/Date';
import Loading from '../../../../components/common/Loading.vue';
import NoteBox from '../../../../components/common/NoteBox.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-empty-data': EmptyData,
        'kgr-button': KgrButton,
        'kgr-checkbox': Checkbox,
        'kgr-icon': Icon,
        'kgr-generic-modal': GenericModal,
        'kgr-loading': Loading,
        'kgr-note-box': NoteBox,
    },
    props: {
        kindergartenId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            selectedChildren: [],
            loading: false,
            error: new Error(),
            batchData: [],
            showSaveModal: false,
            pending: false,
            batchResult: null,
        };
    },
    computed: {
        allChecked() {
            return this.selectedChildren.length === this.batchData.length;
        },
        tableBatchData() {
            const rows = [];
            this.batchData.forEach((data, index) => {
                const memberships = data.memberships ?? [null];
                const row = {
                    rowIndex: index,
                    rowspan: Math.max(data.memberships.length, 1),
                    child: data.child,
                    membership: memberships[0],
                };
                rows.push(row);
                memberships.slice(1).forEach((m) => {
                    rows.push({
                        rowIndex: index,
                        child: data.child,
                        membership: m,
                    });
                });
            });

            return rows;
        },
    },
    mounted() {
        this.loadBatchData();
    },
    methods: {
        getFailedTooltip(childId) {
            if (!this.hasFailed(childId)) {
                return '';
            }
            const messages = this.batchResult.find((r) => r.childId === childId && r.success === false)?.messages;
            if (!messages) {
                console.error('could not find failure messages for childId', childId);
                return '';
            }
            return messages.join(' ');
        },
        hasFailed(childId) {
            return this.batchResult !== null && this.batchResult.find((r) => r.childId === childId)?.success === false;
        },
        hasSucceeded(childId, membershipId) {
            console.log(childId, membershipId);
            return this.batchResult !== null && this.batchResult.find((r) => r.childId === childId && r.membershipId === membershipId)?.success === true;
        },
        openSaveModal() {
            this.showSaveModal = true;
            this.batchResult = null;
        },
        finishSaveModal() {
            this.showSaveModal = false;
        },
        toggleAll() {
            if (this.allChecked) {
                this.selectedChildren = [];
                return;
            }
            this.selectedChildren = this.batchData.map((data) => data.child.id);
            this.$forceUpdate();
        },
        toggleChild(child) {
            if (this.selectedChildren.includes(child.id)) {
                this.selectedChildren = this.selectedChildren.filter((id) => id !== child.id);
                return;
            }
            this.selectedChildren.push(child.id);
        },
        generate() {
            if (this.pending) {
                return;
            }
            this.pending = true;
            axios.post(`/kindergartens/${this.kindergartenId}/baykibig/child-year-batch`, {
                childIds: this.selectedChildren,
            })
                .then((response) => {
                    this.batchResult = response.data.batchResults;
                    const hasFailure = this.batchResult.filter((r) => !r.success).length > 0;
                    const hasSuccess = this.batchResult.filter((r) => r.success).length > 0;
                    if (hasSuccess && !hasFailure) {
                        this.$notify.success('saveChanges.success');
                    } else if (hasSuccess && hasFailure) {
                        this.$notify.warning('Es wurden nicht alle Änderungen gespeichert.');
                    } else {
                        this.$notify.error('saveChanges.error');
                    }
                    this.loadBatchData();
                })
                .catch(() => {
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => {
                    this.pending = false;
                });
        },
        loadBatchData() {
            this.loading = true;
            axios.get(`/kindergartens/${this.kindergartenId}/baykibig/child-year-batch`)
                .then((response) => {
                    this.batchData = response.data.data.map((data) => ({
                        child: {
                            id: data.child.id,
                            firstName: data.child.firstName,
                            lastName: data.child.lastName,
                        },
                        memberships: data.memberships.map((m) => {
                            m.start = KgrDate.fromIso8601(m.start);
                            m.end = KgrDate.fromIso8601(m.end);
                            return m;
                        }),
                    }));
                })
                .catch(() => {
                    this.$notify.error('error.loadingData');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style scoped>
div.kgr-checkbox {
    border-left: 6px solid transparent;
}
tr.batch-result-fail {
    border-left: 6px solid red;
}
tr.batch-result-success {
    border-left: 6px solid green;
}
tr.batch-result-success td {
    background-color: #d4fedb;
}
tr.batch-result-fail td {
    background-color: #fed4da;
}
</style>
